import React, { useState } from 'react'

import PropTypes from 'prop-types'
import { Button } from '@material-ui/core'
import { useHistory } from 'react-router-dom'
import { reverse } from 'named-urls'

import {
  ConfirmationDialog,
  LoadingFeedback,
  MenuButton,
  Permitted,
} from 'components'

import useSnackbar from 'hooks/useSnackbar'
import useThirdPartyManagementStepper from 'hooks/useThirdPartyManagementStepper'

import { routes } from 'Routes'
import permissions from 'constants/permissions'
import constants from 'constants/index'
import * as service from 'service'
import helpers from 'helpers'

const Actions = ({ companySupplierId, audit, auditsCount }) => {
  const { refresh, isShow } = useThirdPartyManagementStepper()
  const [loading, setLoading] = useState(false)
  const [openDestroyDialog, setOpenDestroyDialog] = useState(false)
  const [openInactivateDialog, setOpenInactivateDialog] = useState(false)
  const [openFinalizeDialog, setOpenFinalizeDialog] = useState(false)
  const [openReopenDialog, setOpenReopenDialog] = useState(false)

  const history = useHistory()
  const snackbar = useSnackbar()

  const { INACTIVE_STATUS, ACTIVE_STATUS, COMPLETED_PROGRESS_STATUS } =
    constants.companySuppliers

  const auditParams = {
    companySupplierId,
    companySupplierAuditId: audit?.id,
  }

  // RV-13: finalizar não inativa a auditoria (o back mexe só no progress_status), então ela fica
  // `active` + `completed` — fora de toda condição do menu: sem Editar, sem Finalizar, sem Ativar.
  // Era um beco sem saída. `finalizedAt` é o discriminador de finalização MANUAL: auditoria concluída
  // por cálculo não tem o campo e continua editável, e por isso não ganha o Reabrir.
  const isManuallyFinalized = !!audit?.finalizedAt

  const openSuccessSnackbar = (message) =>
    snackbar.open({ message, variant: 'success' })

  const openErrorSnackbar = (error, message) =>
    snackbar.open({
      message:
        helpers.formatters.errorMessage(error?.response?.data?.error) ||
        message,
      variant: 'error',
    })

  const handleView = () =>
    history.push(
      reverse(routes.thirdPartyManagements.mySuppliers.audit.show, auditParams),
      {
        fromPathname: history.location.pathname,
        fromInitialStep: isShow ? 2 : 1,
      },
    )

  const handleEdit = () =>
    history.push(
      reverse(routes.thirdPartyManagements.mySuppliers.audit.edit, auditParams),
    )

  const handleFinalize = async () => {
    try {
      setLoading(true)
      await service.dponet.suppliers.audits.finalize(auditParams)

      openSuccessSnackbar('Auditoria finalizada com sucesso')
      setOpenFinalizeDialog(false)
      refresh()
    } catch (error) {
      openErrorSnackbar(error, 'Ocorreu um erro ao finalizar a auditoria')
    } finally {
      setLoading(false)
    }
  }

  const handleInactivate = async () => {
    try {
      setLoading(true)
      await service.dponet.suppliers.audits.inactivate(auditParams)

      openSuccessSnackbar('Auditoria inativada com sucesso')
      setOpenInactivateDialog(false)
      refresh()
    } catch (error) {
      openErrorSnackbar(error, 'Ocorreu um erro ao inativar a auditoria')
    } finally {
      setLoading(false)
    }
  }

  const handleReactivate = async () => {
    try {
      setLoading(true)
      await service.dponet.suppliers.audits.reactivate(auditParams)

      openSuccessSnackbar('Auditoria reativada com sucesso')
      refresh()
    } catch (error) {
      openErrorSnackbar(error, 'Ocorreu um erro ao reativar a auditoria')
    } finally {
      setLoading(false)
    }
  }

  const handleReopen = async () => {
    try {
      setLoading(true)
      await service.dponet.suppliers.audits.reopen(auditParams)

      openSuccessSnackbar('Auditoria reaberta com sucesso')
      setOpenReopenDialog(false)
      refresh()
    } catch (error) {
      openErrorSnackbar(error, 'Ocorreu um erro ao reabrir a auditoria')
    } finally {
      setLoading(false)
    }
  }

  const handleDestroy = async () => {
    try {
      setLoading(true)
      await service.dponet.suppliers.audits.destroy(auditParams)

      openSuccessSnackbar('Auditoria excluída com sucesso')
      setOpenDestroyDialog(false)
      refresh()
    } catch (error) {
      openErrorSnackbar(error, 'Ocorreu um erro ao excluir a auditoria')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <LoadingFeedback open={loading} />
      <MenuButton iconButtonSize="small">
        <Button color="secondary" fullWidth size="small" onClick={handleView}>
          Visualizar
        </Button>

        {!isShow && (
          <Permitted tag={permissions.COMPANY_SUPPLIERS.UPDATE}>
            {audit?.status === ACTIVE_STATUS &&
              audit?.progressStatus !== COMPLETED_PROGRESS_STATUS && (
                <>
                  <Button
                    color="secondary"
                    fullWidth
                    size="small"
                    onClick={handleEdit}
                  >
                    Editar
                  </Button>
                  <Button
                    color="secondary"
                    fullWidth
                    size="small"
                    onClick={() => setOpenFinalizeDialog(true)}
                  >
                    Finalizar
                  </Button>

                  {auditsCount > 1 && (
                    <Button
                      color="secondary"
                      fullWidth
                      size="small"
                      onClick={() => setOpenInactivateDialog(true)}
                    >
                      Inativar
                    </Button>
                  )}
                </>
              )}

            {/*
              Porta de volta depois da reabertura: quando a avaliação já estava toda respondida,
              update_progress_status! a devolve como `completed` por CÁLCULO — sem finalizedAt. Nesse
              estado o bloco acima some (exige progressStatus !== COMPLETED), o Reabrir some (exige
              finalizedAt) e o Ativar some (exige INACTIVE): sobrava só Visualizar, que é o mesmo beco
              sem saída que o RV-13 veio corrigir. Vale também para avaliação que auto-concluiu sem
              nunca ter sido finalizada — a DI-03 diz que esse estado é editável, então poder
              finalizá-lo à mão é coerente.
            */}
            {audit?.status === ACTIVE_STATUS &&
              audit?.progressStatus === COMPLETED_PROGRESS_STATUS &&
              !isManuallyFinalized && (
                <Button
                  color="secondary"
                  fullWidth
                  size="small"
                  onClick={() => setOpenFinalizeDialog(true)}
                >
                  Finalizar
                </Button>
              )}

            {audit?.status === ACTIVE_STATUS && isManuallyFinalized && (
              <Button
                color="secondary"
                fullWidth
                size="small"
                onClick={() => setOpenReopenDialog(true)}
              >
                Reabrir
              </Button>
            )}

            {audit?.status === INACTIVE_STATUS && (
              <Button
                color="secondary"
                fullWidth
                size="small"
                onClick={handleReactivate}
              >
                Ativar
              </Button>
            )}

            {auditsCount > 1 &&
              audit?.progressStatus !== COMPLETED_PROGRESS_STATUS && (
                <Button
                  color="secondary"
                  fullWidth
                  size="small"
                  onClick={() => setOpenDestroyDialog(true)}
                >
                  Excluir
                </Button>
              )}
          </Permitted>
        )}
      </MenuButton>

      {openFinalizeDialog && (
        <ConfirmationDialog
          open={openFinalizeDialog}
          setOpen={setOpenFinalizeDialog}
          text="Deseja confirmar a finalização da auditoria?"
          actionAcceptButton={handleFinalize}
          loading={loading}
        />
      )}

      {openReopenDialog && (
        <ConfirmationDialog
          open={openReopenDialog}
          setOpen={setOpenReopenDialog}
          text="A auditoria voltará a aceitar respostas. O fornecedor não será notificado e nenhuma cobrança será reagendada. Deseja confirmar a reabertura?"
          actionAcceptButton={handleReopen}
          loading={loading}
        />
      )}

      {openInactivateDialog && (
        <ConfirmationDialog
          open={openInactivateDialog}
          setOpen={setOpenInactivateDialog}
          text="Deseja confirmar a inativação da auditoria?"
          actionAcceptButton={handleInactivate}
          loading={loading}
        />
      )}

      {openDestroyDialog && (
        <ConfirmationDialog
          open={openDestroyDialog}
          setOpen={setOpenDestroyDialog}
          text="Esta ação não poderá ser desfeita. Deseja confirmar a exclusão da auditoria?"
          actionAcceptButton={handleDestroy}
          variant="error"
          loading={loading}
        />
      )}
    </>
  )
}

Actions.propTypes = {
  companySupplierId: PropTypes.number,
  audit: PropTypes.object,
  auditsCount: PropTypes.number,
}

Actions.defaultProps = {
  auditsCount: 0,
}

export default Actions
